import React, { useState, useCallback, useMemo } from 'react';
import { ContractExplorer } from './ContractExplorer';
import { RegisteredContractsList } from './RegisteredContractsList';
import { ExecutionRuleManager } from './ExecutionRuleManager';
import { GeoLinkPendingDepositsPanel } from './GeoLinkPendingDepositsPanel';
import { GeoLinkPendingTradesPanel } from './GeoLinkPendingTradesPanel';
import { GeoLinkRulesPanel } from './GeoLinkRulesPanel';
import { prepareDepositXdr } from '../v2/services/pendingDepositService';
import type { SmartAccountRecord } from '../services/passkeyKitService';
import type { PendingDeposit } from '../v2/types';
import './SmartContractsOverlay.css';

interface SmartContractsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  walletAddress: string | null;
  /** Connected wallet (Freighter wrapper) — has signTransaction(xdr, passphrase) */
  wallet?: { signTransaction: (xdr: string, passphrase: string) => Promise<string | { signedTransaction: string }> } | null;
  /** PasskeyKit service — for smart-account (C-address) users */
  passkeyKitService?: {
    getAccounts: () => SmartAccountRecord[];
    signUnsignedXdr: (xdr: string, account: SmartAccountRecord, latestLedger?: number) => Promise<string>;
    buildAndSignArbitraryXdr?: (account: SmartAccountRecord, unsignedXdr: string) => Promise<string>;
  } | null;
  allowedCountries?: Set<number>;
}

type Tab = 'explorer' | 'registered' | 'rules' | 'pending-rules' | 'deposits' | 'trades' | 'completed' | 'rejected';

const TABS: { id: Tab; label: string; shortLabel: string; icon: string; requiresWallet: boolean }[] = [
  { id: 'explorer',      label: 'Explore',         shortLabel: 'Explore',    icon: '🔍', requiresWallet: false },
  { id: 'registered',    label: 'Registered',      shortLabel: 'Registered', icon: '📋', requiresWallet: true  },
  { id: 'rules',         label: 'Execution Rules', shortLabel: 'Rules',      icon: '⚡', requiresWallet: false },
  { id: 'pending-rules', label: 'Pending Rules',   shortLabel: 'Pending',    icon: '⏳', requiresWallet: true  },
  { id: 'deposits',      label: 'Deposits',        shortLabel: 'Deposits',   icon: '💳', requiresWallet: true  },
  { id: 'trades',        label: 'Zenex Trades',    shortLabel: 'Trades',     icon: '📈', requiresWallet: true  },
  { id: 'completed',     label: 'Completed',       shortLabel: 'Done',       icon: '✓',  requiresWallet: true  },
  { id: 'rejected',      label: 'Rejected',        shortLabel: 'Rejected',   icon: '✕',  requiresWallet: true  },
];

export const SmartContractsOverlay: React.FC<SmartContractsOverlayProps> = ({
  isOpen,
  onClose,
  walletAddress,
  wallet,
  passkeyKitService,
  allowedCountries,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('explorer');

  /**
   * Universal signing callback passed to GeoLinkPendingDepositsPanel.
   * Returns { signedXdr, useServiceAccount } so the panel knows which
   * settlement path to use.
   *
   * Signing strategies:
   *   A) Passkey (smart-account, C-address):
   *      - Backend builds XDR with SERVICE ACCOUNT as source/fee-payer
   *      - Frontend signs only the Soroban auth entry via WebAuthn
   *      - Backend adds service-account signature before Stellar submit
   *   B) Freighter (G-address):
   *      - Backend builds XDR with USER'S key as source
   *      - Frontend fully signs the transaction via Freighter
   *      - Backend submits the complete signed XDR directly
   */
  // ── Passkey signer ────────────────────────────────────────────────────────
  const handleSignXdrPasskey = useCallback(async (
    deposit: PendingDeposit, amountStroops: number,
  ): Promise<{ signedXdr: string; useServiceAccount: boolean }> => {
    if (!passkeyKitService) throw new Error('Passkey service not available. Reload the page and try again.');
    const accounts = passkeyKitService.getAccounts();
    if (!accounts.length) throw new Error('No passkey account found. Register a passkey first.');
    // accountAddress is the smart-contract C-address; walletAddress is the linked Freighter G-address
    const passKeyAddr = walletAddress?.startsWith('C') ? walletAddress : accounts[0].accountAddress;
    const prep = await prepareDepositXdr(deposit.pendingDepositId, amountStroops, passKeyAddr, { useServiceAccount: true });
    if (!prep.success || !prep.unsignedXdr) throw new Error(prep.error || 'Failed to prepare deposit transaction');
    // Find the passkey account record by its contract address (accountAddress), not walletAddress
    const account = accounts.find(a => a.accountAddress === passKeyAddr) ?? accounts[0];
    const signedXdr = await passkeyKitService.signUnsignedXdr(prep.unsignedXdr, account, prep.latestLedger);
    return { signedXdr, useServiceAccount: true };
  }, [walletAddress, passkeyKitService]);

  // ── Wallet signer ─────────────────────────────────────────────────────────
  const handleSignXdrWallet = useCallback(async (
    deposit: PendingDeposit, amountStroops: number,
  ): Promise<{ signedXdr: string; useServiceAccount: boolean }> => {
    if (!wallet || !walletAddress) throw new Error('No wallet connected');
    const prep = await prepareDepositXdr(deposit.pendingDepositId, amountStroops, walletAddress, { useServiceAccount: false });
    if (!prep.success || !prep.unsignedXdr) throw new Error(prep.error || 'Failed to prepare deposit transaction');
    const networkPassphrase = deposit.network === 'mainnet'
      ? 'Public Global Stellar Network ; September 2015'
      : 'Test SDF Network ; September 2015';
    const result = await wallet.signTransaction(prep.unsignedXdr, networkPassphrase);
    const signedXdr = typeof result === 'string' ? result : result.signedTransaction;
    return { signedXdr, useServiceAccount: false };
  }, [walletAddress, wallet]);

  // ── Auto-detect signer (legacy, used when only one method is connected) ───
  const handleSignXdr = useCallback(async (
    deposit: PendingDeposit, amountStroops: number,
  ): Promise<{ signedXdr: string; useServiceAccount: boolean }> => {
    if (!walletAddress) throw new Error('No wallet connected');
    if (walletAddress.startsWith('C')) return handleSignXdrPasskey(deposit, amountStroops);
    return handleSignXdrWallet(deposit, amountStroops);
  }, [walletAddress, handleSignXdrPasskey, handleSignXdrWallet]);

  const hasBothSigners = !!(wallet && passkeyKitService && passkeyKitService.getAccounts().length > 0);

  // ── Signers for the Zenex trades panel ─────────────────────────────────────
  // Zenex's `open_market` / `close_position` XDR comes from the GeoTrust
  // backend's `/api/v2/zenex/prepare-*` endpoints, not from the pending
  // deposits service. We just forward the unsigned XDR to whichever signer
  // is available for the user.
  const signZenexPasskey = useCallback(async (
    unsignedXdr: string, latestLedger?: number,
  ): Promise<string> => {
    if (!passkeyKitService) throw new Error('Passkey service not available.');
    const accounts = passkeyKitService.getAccounts();
    if (!accounts.length) throw new Error('No passkey account registered.');
    const preferred = walletAddress?.startsWith('C')
      ? accounts.find(a => a.accountAddress === walletAddress) ?? accounts[0]
      : accounts[0];
    return passkeyKitService.signUnsignedXdr(unsignedXdr, preferred, latestLedger);
  }, [walletAddress, passkeyKitService]);

  const signZenexClassic = useCallback(async (
    unsignedXdr: string, networkPassphrase: string,
  ): Promise<string> => {
    if (!wallet) throw new Error('No wallet connected');
    const res = await wallet.signTransaction(unsignedXdr, networkPassphrase);
    return typeof res === 'string' ? res : res.signedTransaction;
  }, [wallet]);

  const activePasskey = useMemo(() => {
    if (!passkeyKitService) return null;
    const accounts = passkeyKitService.getAccounts();
    if (!accounts.length) return null;
    return walletAddress?.startsWith('C')
      ? accounts.find(a => a.accountAddress === walletAddress) ?? accounts[0]
      : accounts[0];
  }, [walletAddress, passkeyKitService]);

  if (!isOpen) return null;

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="smart-contracts-overlay" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">
          <h2>Smart Contracts</h2>
          <button className="overlay-close-button" onClick={onClose}>×</button>
        </div>

        {/* Tab bar — responsive grid on mobile, flex row on desktop */}
        <div className="smart-contracts-tabs">
          {TABS.map(tab => {
            const locked = tab.requiresWallet && !walletAddress;
            return (
              <button
                key={tab.id}
                className={`smart-contracts-tab ${activeTab === tab.id ? 'active' : ''} ${locked ? 'locked' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                disabled={locked}
                title={locked ? 'Connect a wallet to access' : tab.label}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label-full">{tab.label}{locked ? ' 🔒' : ''}</span>
                <span className="tab-label-short">{tab.shortLabel}{locked ? ' 🔒' : ''}</span>
              </button>
            );
          })}
        </div>

        <div className="smart-contracts-content">

          {/* ── Explore (discover-by-address) ────────────────────────── */}
          {activeTab === 'explorer' && (
            <div className="smart-contracts-tab-content">
              <ContractExplorer />
            </div>
          )}

          {/* ── Registered Contracts (this wallet's inventory) ───────── */}
          {activeTab === 'registered' && (
            <div className="smart-contracts-tab-content">
              {walletAddress
                ? <RegisteredContractsList walletAddress={walletAddress} />
                : <WalletRequired />}
            </div>
          )}

          {/* ── Execution Rules ──────────────────────────────────────── */}
          {activeTab === 'rules' && (
            <div className="smart-contracts-tab-content">
              {walletAddress
                ? <ExecutionRuleManager walletAddress={walletAddress} />
                : <WalletRequired />}
            </div>
          )}

          {/* ── Pending Rules (non-deposit) ──────────────────────────── */}
          {activeTab === 'pending-rules' && (
            <div className="smart-contracts-tab-content">
              {walletAddress
                ? <GeoLinkRulesPanel mode="pending" walletPublicKey={walletAddress} pollIntervalMs={10_000} allowedCountries={allowedCountries} />
                : <WalletRequired />}
            </div>
          )}

          {/* ── Pending Deposits ─────────────────────────────────────── */}
          {activeTab === 'deposits' && (
            <div className="smart-contracts-tab-content">
              {walletAddress
                ? <GeoLinkPendingDepositsPanel
                    walletPublicKey={walletAddress}
                    passkeyAccountAddress={
                      walletAddress?.startsWith('C')
                        ? walletAddress
                        : ((passkeyKitService as any)?.getAccounts?.()[0]?.accountAddress ?? null)
                    }
                    network="testnet"
                    pollIntervalMs={15_000}
                    onSignXdr={hasBothSigners ? undefined : handleSignXdr}
                    onSignXdrWallet={hasBothSigners ? handleSignXdrWallet : undefined}
                    onSignXdrPasskey={hasBothSigners ? handleSignXdrPasskey : undefined}
                    hasBothSigners={hasBothSigners}
                    allowedCountries={allowedCountries}
                  />
                : <WalletRequired />}
            </div>
          )}

          {/* ── Zenex Trades ────────────────────────────────────────── */}
          {activeTab === 'trades' && (
            <div className="smart-contracts-tab-content">
              {walletAddress
                ? <GeoLinkPendingTradesPanel
                    walletPublicKey={walletAddress}
                    network="testnet"
                    pollIntervalMs={15_000}
                    signUnsignedXdr={passkeyKitService ? signZenexPasskey : undefined}
                    signClassicXdr={wallet ? signZenexClassic : undefined}
                    activePasskey={activePasskey}
                  />
                : <WalletRequired />}
            </div>
          )}

          {/* ── Completed ────────────────────────────────────────────── */}
          {activeTab === 'completed' && (
            <div className="smart-contracts-tab-content">
              {walletAddress
                ? <GeoLinkRulesPanel mode="completed" walletPublicKey={walletAddress} pollIntervalMs={30_000} allowedCountries={allowedCountries} />
                : <WalletRequired />}
            </div>
          )}

          {/* ── Rejected ─────────────────────────────────────────────── */}
          {activeTab === 'rejected' && (
            <div className="smart-contracts-tab-content">
              {walletAddress
                ? <GeoLinkRulesPanel mode="rejected" walletPublicKey={walletAddress} pollIntervalMs={30_000} allowedCountries={allowedCountries} />
                : <WalletRequired />}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

const WalletRequired: React.FC = () => (
  <div style={{
    padding: '40px 24px', textAlign: 'center',
    color: '#888', fontSize: '13px', lineHeight: '1.8',
  }}>
    <div style={{ fontSize: '28px', marginBottom: '10px' }}>🔒</div>
    <div style={{ fontWeight: 600, color: '#555', marginBottom: '4px' }}>Wallet Required</div>
    Please connect your wallet to access this section.
  </div>
);
